import { useEffect } from "react";
import { useRouter } from "next/router";
import Link from "next/link";

// hooks / utils
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { usePublicCompany } from "@/hooks/use-public-company";

// ui
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";

// components
import { Layout5050 } from "@/components/layouts/layout-50-50";

const CARD_INFO = {
  title: "WELCOME TO",
  description: "Log in to view your estimates, pay invoices, and more.",
};

const loginSchema = z.object({
  username: z
    .string()
    .email("Invalid email address")
    .min(1, "Email is required"),
  password: z.string().min(1, "Password is required"),
  // NOTE: JT - we're going to hold off on this for now
  // rememberMe: z.boolean().optional(),
});

// Infer the type from the schema
type LoginFormValues = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const router = useRouter();
  const {
    companyId: rawCompanyId,
    estimateId,
    pathname,
    ...rest
  } = router.query;

  const companyId = rawCompanyId ? Number(rawCompanyId) : null;

  const redirectToPathname = () => {
    const queryParams = new URLSearchParams(rest as Record<string, string>);
    const queryString = queryParams.toString();
    const targetPath = (pathname as string) || "/";

    router.replace(queryString ? `${targetPath}?${queryString}` : targetPath);
  };

  const { data: company, isFetched } = usePublicCompany(companyId as number);

  // Initialize the form with react-hook-form and zod resolver
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: "",
      password: "",
      // rememberMe: false, // how does this work?
    },
  });

  const handleSubmit = (data: LoginFormValues) => {
    const { username, password } = data;
    loginMutation.mutate({ username, password });
  };

  const loginMutation = useMutation({
    mutationFn: async (data: LoginFormValues) => {
      if (!companyId || Array.isArray(companyId) || Number.isNaN(companyId)) {
        throw new Error("Invalid customer ID");
      }

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/customer/auth/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...data,
            companyId,
          }),
          credentials: "include",
        },
      );

      const responseData = (await response.json()) as { message: string };
      if (!response.ok) {
        throw new Error(responseData.message || "Login failed");
      }

      return responseData;
    },
    onSuccess: () => {
      if (estimateId) {
        // this isn't supported in the legacy app... doesn't hurt to support it though
        router.push(`/estimates/view/${estimateId}`);
      } else if (pathname) {
        redirectToPathname();
      } else {
        router.push("/estimates");
      }
    },
    onError: (error: Error) => {
      console.error("Login mutation error:", error);
      form.setError("root", {
        type: "manual",
        message: error.message || "Login failed. Please try again.",
      });
    },
  });

  // if companyId is a string or symbol, it's invalid
  const invalidCompanyId = router.isReady && Number.isNaN(companyId);

  // if the company is invalid, redirect to the 404 page
  useEffect(() => {
    if (invalidCompanyId || (isFetched && !company)) {
      router.replace("/404");
    }
  }, [invalidCompanyId, isFetched, company, router]);

  // we could add a loading state here if we want
  if (!isFetched || !company) {
    return null;
  }

  // if the company is banned, show the in-active card
  if (company?.isBanned) {
    const inActiveCardInfo = {
      title: company?.name ?? "",
      description:
        "The customer portal is unavailable at this time, please contact our office for support",
      intent: "danger",
      badge: "CUSTOMER PORTAL UNAVAILABLE",
    } as const;

    return <Layout5050 companyId={companyId} cardInfo={inActiveCardInfo} />;
  }

  // if the company is active, show the login card
  return (
    <Layout5050 companyId={companyId} cardInfo={CARD_INFO}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="">
          <div className="flex flex-col gap-4">
            {form.formState.errors.root && (
              <Alert variant="destructive" className="p-2 text-red-950 w-max">
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex flex-col gap-2">
                      <FormLabel className="text-sm font-medium">
                        Email
                      </FormLabel>
                      <Input
                        {...field}
                        className="h-10"
                        placeholder="Username"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex flex-col gap-2">
                      <div className="flex justify-between">
                        <FormLabel className="text-sm font-medium">
                          Password
                        </FormLabel>
                        <FormLabel className="text-sm">
                          <Link
                            href={`/forget?co=${companyId}`}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            Forgot Password
                          </Link>
                        </FormLabel>
                      </div>
                      <Input
                        {...field}
                        type="password"
                        placeholder="Password"
                        className="h-10"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* <FormField
              control={form.control}
              name="rememberMe"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                      }}
                    />
                  </FormControl>
                  <FormLabel className="text-sm text-gray-600">
                    Remember me for 30 days
                  </FormLabel>
                </FormItem>
              )}
            /> */}

            <Button
              type="submit"
              intent="action"
              className="w-full bg-legacyPrimary"
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? "Logging in..." : "Login"}
            </Button>
          </div>
        </form>
      </Form>
    </Layout5050>
  );
}
